import { CallCorpusRequest, type CallCorpusRequest as Feedback } from '@jabberr/core';
import { api } from './api.js';
interface Pending { meetingId: string; payload: Feedback; key?: string }
const storageKey = (owner: string) => `jabberr-feedback-v1:${owner}`;
export function pendingFeedback(owner: string): Pending[] {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(storageKey(owner)) ?? '[]');
    if (!Array.isArray(raw) || raw.length > 100) return [];
    return raw.filter((v): v is Pending => typeof v?.meetingId === 'string' && /^[0-9a-f-]{36}$/i.test(v.meetingId) && CallCorpusRequest.safeParse(v.payload).success);
  } catch { return []; }
}
const store = (owner: string, rows: Pending[]) => localStorage.setItem(storageKey(owner), JSON.stringify(rows));
const running = new Map<string, Promise<void>>();
export function retryFeedback(owner: string): Promise<void> {
  const current = running.get(owner);
  if (current) return current;
  const attempt = (async () => {
    const me = await api.me().catch(() => null);
    if (me?.user.id !== owner) return;
    for (const row of pendingFeedback(owner)) {
      try {
        await api.meetings.corpus(row.meetingId, row.payload);
        store(owner, pendingFeedback(owner).filter(x => x.payload.submissionId !== row.payload.submissionId));
      } catch { /* Keep the immutable submission for a later authenticated retry. */ }
    }
  })().finally(() => running.delete(owner));
  running.set(owner, attempt);
  return attempt;
}
export async function submitFeedback(owner: string, meetingId: string, payload: Feedback, key?: string): Promise<boolean> {
  payload = { ...payload, expectedActorId: owner };
  const rows = pendingFeedback(owner);
  const existing = rows.find(x => x.meetingId === meetingId && (x.payload.submissionId === payload.submissionId || key && x.key === key));
  if (!existing) {
    if (rows.length >= 100) throw new Error('Feedback retry storage is full. Retry pending feedback first.');
    store(owner, [...rows, { meetingId, payload: CallCorpusRequest.parse(payload), ...(key ? { key } : {}) }]);
  }
  await retryFeedback(owner);
  return !pendingFeedback(owner).some(x => x.payload.submissionId === (existing?.payload.submissionId ?? payload.submissionId));
}

/** Stable IDs avoid a second contribution/moment row after a reload or lost local receipt. */
export async function signalSubmissionId(owner: string, meetingId: string, signal: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`${owner}:${meetingId}:${signal}`));
  const hex = Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0,8)}-${hex.slice(8,12)}-4${hex.slice(13,16)}-a${hex.slice(17,20)}-${hex.slice(20,32)}`;
}
